import * as React from 'react';
import styled, { css } from 'styled-components';

import { Spinner } from 'components/common';
import { MarkdownPreview } from 'components/common/MarkdownEditor';
import { useGetDecoratedEventProcedure } from 'security-app/hooks/useEventProceduresAPI';
import type { Event } from 'components/events/events/types';

import EventProcedureStepActionButton from './EventProcedureStepActionButton';

type Props = {
  eventDefinitionEventProcedure: string | undefined;
  eventId?: string;
  event?: Event;
};

const EventProcedureStepContainer = styled.div(
  ({ theme }) => css`
    display: flex;
    padding: 0.5rem 1rem;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    border-radius: 3px;
    border: 1px solid ${theme.colors.misc.divider};
    margin: 4px 0;
  `,
);

const EventProcedureStepInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 3;
`;

const EventProcedureSection = styled.div`
  width: 100%;
  margin: 1rem 0;
`;

function EventProcedureSummary({ eventDefinitionEventProcedure, eventId = '', event = undefined }: Props) {
  const { eventProcedure, loadingEventProcedure } = useGetDecoratedEventProcedure(
    eventDefinitionEventProcedure,
    event?.id ?? eventId,
  );

  if (eventProcedure === null) {
    return <div>You do not have the required permissions to view this event procedure.</div>;
  }

  if (!eventDefinitionEventProcedure) {
    return <div>This Event does not have any Event Procedures yet.</div>;
  }

  return (
    <>
      {loadingEventProcedure ? (
        <Spinner text="Loading Event Procedure" />
      ) : (
        <>
          <EventProcedureSection>
            <strong>Title</strong>
            <div>{eventProcedure.title}</div>
          </EventProcedureSection>
          <EventProcedureSection>
            <strong>Description</strong>
            <MarkdownPreview
              show
              withFullView
              noBorder
              noBackground
              value={eventProcedure?.description || 'No description'}
            />
          </EventProcedureSection>
          <EventProcedureSection>
            <strong>Steps</strong>
            {eventProcedure?.steps.map((step, index) => (
              <EventProcedureStepContainer key={step.id}>
                <EventProcedureStepInfo>
                  <strong>{`${index + 1}. ${step.title}`}</strong>
                  <div>{step.description}</div>
                </EventProcedureStepInfo>
                <EventProcedureStepActionButton step={step} event={event} />
              </EventProcedureStepContainer>
            ))}
          </EventProcedureSection>
        </>
      )}
    </>
  );
}

export default EventProcedureSummary;
